import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { getConfig, mcpUrl } from "@/lib/config";
import { logApplicationFailure } from "@/lib/user-error";

// Claude integration: tells the user how to connect their Claude apps to this
// tenant's MCP endpoint. Distinct from the in-app Assistant (that talks to
// agent-service; this exposes genesis tools directly over the MCP endpoint).
// The client id is a PUBLIC (secret-less, PKCE) Keycloak client, so everything
// shown here is safe to display. Rendered only when config.mcp.enabled.

function CopyField({ label, value }: { label: string; value: string }) {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      logApplicationFailure("claude-connect.copy");
    }
  }

  return (
    <div>
      <div className="mb-1 text-[12px] text-ink/45">{label}</div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="min-w-0 flex-1 rounded-md bg-page px-2 py-1.5 font-mono text-[12px] outline-none ring-1 ring-ink/10"
        />
        <Button variant="primary" size="sm" onClick={copy} className="shrink-0">
          {copied
            ? intl.formatMessage({ id: "design.copied", defaultMessage: "Copied" })
            : intl.formatMessage({ id: "design.copy", defaultMessage: "Copy" })}
        </Button>
      </div>
    </div>
  );
}

export function ClaudeConnectPanel() {
  const intl = useIntl();
  const url = mcpUrl();
  const clientId = getConfig().mcp.clientId;
  const cliCommand = `claude mcp add --transport http --client-id ${clientId} --callback-port 45454 smycles ${url}`;

  return (
    <div className="box bg-surface p-6">
      <div className="text-[10px] tracking-[0.25em] text-ink/55">
        {intl.formatMessage({ id: "userSettings.claude.eyebrow", defaultMessage: "Claude" })}
      </div>
      <h2 className="mt-1 font-serif text-2xl leading-tight">
        {intl.formatMessage({ id: "userSettings.claude.title", defaultMessage: "Connect Claude" })}
      </h2>
      <p className="mt-2 max-w-xl text-sm text-ink/70">
        {intl.formatMessage({
          id: "userSettings.claude.intro",
          defaultMessage:
            "Use your Smycles data and tools directly inside Claude. Claude sees exactly what you can see; you sign in with your normal Smycles account. This is separate from the assistant built into Smycles.",
        })}
      </p>

      <div className="mt-6 space-y-4">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">
          {intl.formatMessage({
            id: "userSettings.claude.detailsTitle",
            defaultMessage: "Connection details",
          })}
        </div>
        <CopyField
          label={intl.formatMessage({ id: "userSettings.claude.urlLabel", defaultMessage: "Server URL" })}
          value={url}
        />
        <CopyField
          label={intl.formatMessage({
            id: "userSettings.claude.clientIdLabel",
            defaultMessage: "Client ID",
          })}
          value={clientId}
        />
        <p className="text-[11px] text-ink/45">
          {intl.formatMessage({
            id: "userSettings.claude.noSecret",
            defaultMessage: "No client secret is needed. Leave the secret field empty.",
          })}
        </p>
      </div>

      {/* Claude app (account connector). The connector is reachable from Anthropic's cloud,
          so it only works where the MCP endpoint is exposed to the public internet — not on
          internal / intranet-only tenants (see the hint below). */}
      <div className="mt-8">
        <div className="space-y-3">
          <div className="text-[10px] tracking-[0.25em] text-ink/55">
            {intl.formatMessage({
              id: "userSettings.claude.appTitle",
              defaultMessage: "Claude app (desktop, web and mobile)",
            })}
          </div>
          <p className="max-w-xl text-[12px] text-ink/55">
            {intl.formatMessage({
              id: "userSettings.claude.appDesc",
              defaultMessage:
                "Add Smycles once as a connector in your Claude account, then it works across the Claude desktop app, the web app and mobile.",
            })}
          </p>
          <p className="max-w-xl rounded-md bg-amber-500/10 px-3 py-2 text-[12px] text-ink/70 ring-1 ring-amber-500/20">
            {intl.formatMessage({
              id: "userSettings.claude.appInternalHint",
              defaultMessage:
                "Note: Claude connects from its own cloud, so this does not work on internal systems for now — the Smycles server has to be reachable from the public internet. Claude Code (below) works on internal systems today.",
            })}
          </p>
          <ol className="max-w-xl list-decimal space-y-1.5 pl-5 text-[13px] text-ink/80">
            <li>
              {intl.formatMessage({
                id: "userSettings.claude.appStep1",
                defaultMessage: "In Claude, open Settings, then Connectors.",
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: "userSettings.claude.appStep2",
                defaultMessage:
                  "Choose Add custom connector and paste the Server URL above.",
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: "userSettings.claude.appStep3",
                defaultMessage:
                  "Open Advanced settings, enter the Client ID above, and leave the client secret empty.",
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: "userSettings.claude.appStep4",
                defaultMessage:
                  "Add the connector, then Connect and sign in with your Smycles account.",
              })}
            </li>
          </ol>
          <p className="max-w-xl text-[11px] text-ink/45">
            {intl.formatMessage({
              id: "userSettings.claude.appTeamNote",
              defaultMessage:
                "On Team and Enterprise plans an organization owner adds the connector once; everyone else only clicks Connect and signs in.",
            })}
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">
          {intl.formatMessage({
            id: "userSettings.claude.codeTitle",
            defaultMessage: "Claude Code (for developers)",
          })}
        </div>
        <p className="max-w-xl text-[12px] text-ink/55">
          {intl.formatMessage({
            id: "userSettings.claude.codeDesc",
            defaultMessage:
              "Run this command, then start Claude Code, run /mcp, choose smycles and select Authenticate. A browser window opens for the Smycles login.",
          })}
        </p>
        <CopyField
          label={intl.formatMessage({
            id: "userSettings.claude.commandLabel",
            defaultMessage: "Command",
          })}
          value={cliCommand}
        />
      </div>
    </div>
  );
}
